import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import { Handshake, WarningCircle, Check, X, BellRinging } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/ui/DataTable';
import { engApi, RENTAL_STATUS_LABEL, RENTAL_STATUS_BADGE } from './engApi';

export default function EngRentals({ access: accessProp }) {
  const ctx = useOutletContext() || {};
  const access = accessProp || ctx.access;
  const isMember = !!access?.is_member;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  const load = useCallback(() => {
    setLoading(true);
    engApi.rentals().then(setRows).catch(() => toast.error('貸出の取得に失敗しました')).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => (statusFilter === 'all' ? rows : rows.filter((r) => r.status === statusFilter)), [rows, statusFilter]);
  const counts = useMemo(() => {
    const c = { all: rows.length };
    Object.keys(RENTAL_STATUS_LABEL).forEach((s) => { c[s] = rows.filter((r) => r.status === s).length; });
    return c;
  }, [rows]);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || '操作に失敗しました'); }
  };

  const approve = (r) => act(() => engApi.approveRental(r.id), '承認しました');
  const reject = (r) => {
    const reason = window.prompt('却下理由（任意）');
    if (reason === null) return;
    act(() => engApi.rejectRental(r.id, reason || null), '却下しました');
  };
  const doReturn = (r) => {
    const note = window.prompt('返却メモ（任意）');
    if (note === null) return;
    act(() => engApi.returnRental(r.id, note || null), '返却処理しました');
  };
  const cancel = (r) => {
    if (!window.confirm('この申請を取消しますか？')) return;
    act(() => engApi.cancelRental(r.id), '取消しました');
  };

  return (
    <div className="space-y-5" data-testid="eng-rentals-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-zinc-900">設備貸出</h1>
          <p className="text-sm text-zinc-500 mt-1">
            {isMember ? '全部署からの貸出申請の承認・返却管理' : '自分の貸出申請の状況（申請は設備台帳の各設備から）'}
          </p>
        </div>
        {isMember && (
          <Button variant="outline" onClick={async () => {
            try {
              const r = await engApi.runOverdueReminders();
              toast.success(r.sent > 0 ? `延滞リマインダーを${r.sent}件送信しました` : '延滞中の貸出はありません（または22時間以内に送信済み）');
            } catch (e) { toast.error(e?.response?.data?.detail || '送信に失敗しました'); }
          }} className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="eng-rental-remind-btn">
            <BellRinging size={16} className="mr-1" /> 延滞リマインダー送信
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {['all', ...Object.keys(RENTAL_STATUS_LABEL)].map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)} data-testid={`eng-rental-filter-${s}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              statusFilter === s ? 'bg-teal-600 text-white shadow' : 'bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-50'
            }`}>
            {s === 'all' ? 'すべて' : RENTAL_STATUS_LABEL[s]}（{counts[s] || 0}）
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 text-center text-sm text-zinc-400">読込中…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-zinc-300 p-10 text-center" data-testid="eng-rentals-empty">
          <Handshake size={40} weight="duotone" className="text-zinc-300 mx-auto mb-2" />
          <p className="text-sm text-zinc-500">貸出申請がありません</p>
        </div>
      ) : (
        <DataTable
          dataTestId="eng-rentals-table"
          rows={filtered}
          rowKey={(r) => r.id}
          rowTestId={(r) => `eng-rental-row-${r.id}`}
          columns={[
            { key: 'rental_no', header: '番号', width: 'w-24', mono: true, mobileHidden: true,
              cell: (r) => <span className="font-mono text-zinc-500">{r.rental_no}</span> },
            { key: 'equipment_name', header: '設備', primary: true, flex: true,
              cell: (r) => (
                <div className="min-w-0">
                  <span className="font-medium text-zinc-900 line-clamp-2 break-words" title={r.equipment_name}>{r.equipment_name}</span>
                  <span className="ml-2 text-[11px] font-mono text-zinc-400">{r.equip_no}</span>
                </div>
              ) },
            { key: 'requested_by_name', header: '申請者', width: 'w-40',
              cell: (r) => (
                <div>
                  <span>{r.requested_by_name}</span>
                  {r.department_name && <span className="block text-[11px] text-zinc-400">{r.department_name}</span>}
                </div>
              ) },
            { key: 'purpose', header: '用途', flex: true, mobileHidden: true,
              cell: (r) => <span className="text-xs text-zinc-600 line-clamp-2 break-words" title={r.purpose}>{r.purpose}</span> },
            { key: 'expected_return_date', header: '返却予定', width: 'w-28',
              cell: (r) => (
                <span className={`tabular-nums ${r.overdue ? 'text-rose-600 font-bold' : ''}`}>
                  {r.expected_return_date}
                  {r.overdue && <WarningCircle size={13} weight="fill" className="inline ml-0.5 -mt-0.5" />}
                </span>
              ) },
            { key: 'status', header: '状態', width: 'w-28', mobileBadge: true, sortable: false,
              cell: (r) => <Badge variant="outline" className={`${RENTAL_STATUS_BADGE[r.status]} text-[11px]`}>{RENTAL_STATUS_LABEL[r.status]}</Badge> },
            { key: 'actions', header: '操作', actions: true,
              cell: (r) => (
                <>
                  {isMember && r.status === 'pending' && (
                    <>
                      <Button size="sm" onClick={() => approve(r)} className="h-8 px-2.5 bg-teal-700 hover:bg-teal-800" data-testid={`eng-rental-approve-${r.id}`}>
                        <Check size={13} className="mr-1" /> 承認
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => reject(r)} className="h-8 px-2.5 text-rose-600 hover:bg-rose-50" data-testid={`eng-rental-reject-${r.id}`}>
                        <X size={13} className="mr-1" /> 却下
                      </Button>
                    </>
                  )}
                  {isMember && r.status === 'approved' && (
                    <Button size="sm" variant="outline" onClick={() => doReturn(r)} className="h-8 px-2.5 border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid={`eng-rental-return-${r.id}`}>
                      返却処理
                    </Button>
                  )}
                  {r.status === 'pending' && !isMember && (
                    <Button size="sm" variant="outline" onClick={() => cancel(r)} className="h-8 px-2.5" data-testid={`eng-rental-cancel-${r.id}`}>
                      取消
                    </Button>
                  )}
                </>
              ) },
          ]}
          empty={{ text: '貸出申請がありません' }}
        />
      )}
    </div>
  );
}
